import React from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle, CheckCircle2, Clock, FileText, RefreshCw,
  ServerCog, Trash2, Wand2, X,
} from 'lucide-react';
import { type Paper, STATUS_META, formatRelative } from './types';

interface Props {
  paper: Paper | null;
  onClose: () => void;
  onReprocess: (id: string) => void;
  onRemove: (id: string) => void;
  onGenerate: (id: string) => void;
}

/** Right-side slide-in drawer showing per-paper detail + actions. */
export const DetailDrawer: React.FC<Props> = ({
  paper, onClose, onReprocess, onRemove, onGenerate,
}) => {
  if (!paper || typeof document === 'undefined') return null;
  const meta = STATUS_META[paper.status];

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[10000] flex items-stretch justify-end bg-slate-900/45 backdrop-blur-sm animate-[ecFade_.2s_ease]"
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-[420px] overflow-y-auto bg-white p-5 pb-24 shadow-[-24px_0_60px_rgba(0,0,0,0.2)] animate-[ecSlide_.25s_ease]"
        style={{ fontFamily: "'Afacad Flux', system-ui, sans-serif" }}
      >
        {/* Head */}
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
          <div className="min-w-0 flex-1">
            <p className="m-0 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#0e9172]">
              <FileText size={11} /> Past paper
            </p>
            <h3
              className="m-0 mt-2 break-all text-[22px] leading-tight"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              {paper.file_name}
            </h3>
            <p className="m-0 mt-1 text-[12.5px] text-slate-500">
              {paper.course_code} {paper.course_title ? `· ${paper.course_title}` : ''}
              {paper.year ? ` · ${paper.year}` : ''}
              {paper.paper_type ? ` · ${paper.paper_type}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-900 hover:bg-slate-200"
          >
            <X size={16} />
          </button>
        </header>

        {/* Stat tiles */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <Stat label="pages"     value={paper.page_count ?? '—'} />
          <Stat label="chunks"    value={paper.chunk_count ?? '—'} />
          <Stat label="questions" value={paper.question_count ?? '—'} />
        </div>

        {/* Status + meta block */}
        <div className="mt-4 grid gap-2">
          <span className={`inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-extrabold uppercase tracking-wider ${meta.tw}`}>
            {paper.status === 'failed'
              ? <AlertCircle size={11} />
              : <CheckCircle2 size={11} />}
            {meta.label}
          </span>
          {paper.error && (
            <p className="m-0 rounded-lg bg-[#c0392b]/10 px-3 py-2 text-[12px] text-[#c0392b]">
              {paper.error}
            </p>
          )}
          {paper.embedding_model && (
            <p className="m-0 inline-flex items-center gap-1.5 text-[12.5px] text-slate-500">
              <ServerCog size={11} /> Embedded with <strong className="text-slate-900">{paper.embedding_model}</strong>
            </p>
          )}
          {paper.uploaded_at && (
            <p className="m-0 inline-flex items-center gap-1.5 text-[12.5px] text-slate-500">
              <Clock size={11} /> Uploaded {formatRelative(paper.uploaded_at)}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            disabled={paper.status !== 'ready'}
            onClick={() => onGenerate(paper.id)}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#1a5276] px-4 py-2.5 text-[13px] font-bold text-white shadow-[0_6px_16px_rgba(26,82,118,0.25)] hover:bg-[#15436a] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
          >
            <Wand2 size={14} /> Generate questions
          </button>
          <button
            type="button"
            onClick={() => onReprocess(paper.id)}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-[13px] font-bold text-slate-700 hover:bg-slate-100"
          >
            <RefreshCw size={13} /> Reprocess
          </button>
          <button
            type="button"
            onClick={() => onRemove(paper.id)}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#c0392b] px-4 py-2.5 text-[13px] font-bold text-white shadow-[0_6px_16px_rgba(192,57,43,0.22)] hover:bg-[#a4321f]"
          >
            <Trash2 size={13} /> Remove
          </button>
        </div>

        <style>{`
          @keyframes ecFade { from { opacity: 0; } to { opacity: 1; } }
          @keyframes ecSlide { from { transform: translateX(30px); } to { transform: translateX(0); } }
        `}</style>
      </aside>
    </div>,
    document.body,
  );
};

const Stat: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <div className="rounded-2xl bg-slate-50 px-2 py-3 text-center">
    <strong
      className="block text-[22px] leading-none text-slate-900"
      style={{ fontFamily: "'Instrument Serif', serif" }}
    >
      {value}
    </strong>
    <span className="mt-1 block text-[10.5px] font-extrabold uppercase tracking-wider text-slate-500">
      {label}
    </span>
  </div>
);
